import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { Button, Typography, Stack, Box, Divider } from "@mui/material";
import { toast } from "react-hot-toast";
import moment from "moment";
import { useDebounce } from "react-use";
import { FieldText } from "../../../components/fields/Text/Text";
import { calculateProScore } from "./calculateProScore";
import { calculateEngagementScore } from "./calculateEngagementScore";
import axiosInstant from "../../../api";
import { searchBrand } from "../../../api/endpoints";
import { useUser } from "../../../hooks/useUser";
import { isProTeam } from "../../../utils";
import config from "../../../config";
import { handleProfileImageUpdate } from "../../profile/helper";
import { FieldUserAutoComplete } from "../../../components/fields/FieldUserAutocomplete/FieldUserAutocomplete";
import { FieldLabel } from "../../../components/fields/FieldLabel/FieldLabel";
import FormContactInfo from "./formFragments/contactInfo";
import FormDemographics from "./formFragments/demographics";
import FormProfessions from "./formFragments/professions";
import FormQualifications from "./formFragments/qualifications";
import FormSocialReach from "./formFragments/socialReach";
import FormActivity from "./formFragments/activity";
import FormCommunityEngagement from "./formFragments/communityEngagement";
import FormExpertise from "./formFragments/expertise";
import FormInterests from "./formFragments/interests";
import FormKeyPoints from "./formFragments/keyPoints";
import FormIntroVideos from "./formFragments/introVideos";
import { ProFormTabs } from "./tabs";
import { FieldError } from "../../../components/fields/FieldError/FieldError";
import { DragNDropProvider } from "../../../components/dnd/DragDropContext";
import FormBasics from "./formFragments/basics";
import FormDocuments from "./formFragments/documents";
import FieldInput from "./formFragments/FieldInput";
import { IconArrowDown } from "../../../utils/icons";
import FormRow from "./formFragments/row";
import { statusList } from "./formFragments/constants";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";

const UnsavedChangesGuard = () => {
    const { dirty, submitForm, isSubmitting, resetForm, values } = useFormikContext();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [pendingHref, setPendingHref] = useState(null);
    const skipRef = useRef(false);
    const initializedRef = useRef(false);
    const [userDirty, setUserDirty] = useState(false);

    useEffect(() => {
        if (!initializedRef.current) {
            const id = setTimeout(() => {
                resetForm({ values });
                initializedRef.current = true;
            }, 500);
            return () => clearTimeout(id);
        }
        setUserDirty(dirty);
    }, [dirty]);

    useEffect(() => {
        if (!userDirty) return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        const onClick = (e) => {
            if (skipRef.current) return;
            const el = e.target.closest("a[href], [data-nav]");
            const href = el?.getAttribute("href") || el?.getAttribute("data-nav");
            if (!href || /^(http|#|mailto:)/.test(href)) return;
            e.preventDefault();
            e.stopPropagation();
            setPendingHref(href);
            setShowModal(true);
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        document.addEventListener("click", onClick, true);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
            document.removeEventListener("click", onClick, true);
        };
    }, [userDirty]);

    const handleLeave = () => {
        setShowModal(false);
        if (pendingHref) {
            skipRef.current = true;
            navigate(pendingHref);
        }
    };

    const handleSave = async () => {
        await submitForm();
        setShowModal(false);
    };

    return (
        <ConfirmationDialog
            open={showModal}
            onClose={handleLeave}
            onConfirm={handleSave}
            title="Unsaved Changes"
            description="Are you sure you want to leave this page without saving your changes?"
            confirmLabel="Save Changes"
            cancelLabel="Leave"
            disabled={isSubmitting}
        />
    );
};

export default function EditProfileForm(props) {
    const { pro, create, handleSubmit, hideMarginTop, selectedTab, onProUpdate } = props;
    const { role } = useUser();

    let registrationSchema;

    if (!create) {
        registrationSchema = yup.object().shape({
            emailAddress: yup
                .string()
                .typeError("Email Address is required")
                .email("Invalid Email Address")
                .required("Email Address is required"),
            slug: yup.string().nullable()
        });
    } else {
        registrationSchema = yup.object().shape({
            emailAddress: yup.string().email("Invalid Email Address").required("Email Address is required"),
            password: yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
            status: yup.string().oneOf(statusList, "Invalid status").required("Status is required")
        });
    }

    const initValues = useMemo(
        () => ({
            avatar: "",
            status: "Inactive",
            emailAddress: pro.emailAddress || "",
            password: "",
            notifications_disabled: false,
            ...pro,
            usertype: "verified creator",
            dateOfBirth: pro.dateOfBirth ? moment(pro.dateOfBirth).toDate() : null
        }),
        [pro]
    );

    const [professions, setProfessions] = useState(initValues.professions || []);
    const [interests, setInterests] = useState(initValues.interests || []);
    const [expertise, setExpertise] = useState(initValues.expertise || []);
    const [keyPoints, setKeyPoints] = useState(initValues.keyPoints || []);
    const [clients, setClients] = useState(initValues.clients || []);

    // Update state when pro changes (e.g., after refresh or save)
    useEffect(() => {
        if (pro?.expertise !== undefined) {
            setExpertise(pro.expertise || []);
        }
        if (pro?.interests !== undefined) {
            setInterests(pro.interests || []);
        }
        if (pro?.keyPoints !== undefined) {
            setKeyPoints(pro.keyPoints || []);
        }
        if (pro?.professions !== undefined) {
            setProfessions(pro.professions || []);
        }
        if (pro?.clients !== undefined) {
            setClients(pro.clients || []);
        }
    }, [pro]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("professions", professions);
        }
    }, [professions]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("interests", interests);
        }
    }, [interests]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("keyPoints", keyPoints);
        }
    }, [keyPoints]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("expertise", expertise);
        }
    }, [expertise]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("clients", clients);
        }
    }, [clients]);

    const formikRef = useRef(null);
    const expertiseFormRef = useRef(null);
    const interestsFormRef = useRef(null);
    const keyPointsFormRef = useRef(null);
    const professionsFormRef = useRef(null);
    const [brandList, setBrandList] = useState([]);
    const [referredBy, setReferredBy] = useState(initValues.referredBy?.referrer || null);
    const [newAvatarFile, setNewAvatarFile] = useState(null);

    const FormObserver = () => {
        const { values } = useFormikContext();

        const doMath = () => {
            // calculate total reach
            values.totalReach =
                (parseInt(values.facebookFollowers, 10) || 0) +
                (parseInt(values.twitterFollowers, 10) || 0) +
                (parseInt(values.instagramFollowers, 10) || 0) +
                (parseInt(values.tiktokFollowers, 10) || 0) +
                (parseInt(values.youtubeSubscribers, 10) || 0) +
                (parseInt(values.pinterestFollowers, 10) || 0);

            // calculate days since last production
            if (values.lastProductionDate) {
                const diff = moment().diff(moment(values.lastProductionDate));
                const days = Math.floor(moment.duration(diff).as("days"));
                values.daysSinceLastProduction = days;
            }

            // determine tdc link
            values.tdcLink = values.slug
                ? `https://shoppers.thedesirecompany.com/pros/${values.slug}`
                : "Pro requires slug field to be filled out";

            // calculate engagement score
            values.communityEngagementScore = calculateEngagementScore(values);

            // calculate pro score
            values.proScore = calculateProScore(values);

            // calculate tier
            let tier;
            if (values.proScore < 100) {
                tier = 6;
            } else if (values.proScore < 200) {
                tier = 5;
            } else if (values.proScore < 250) {
                tier = 4;
            } else if (values.proScore < 300) {
                tier = 3;
            } else if (values.proScore < 399) {
                tier = 2;
            } else {
                tier = 1;
            }
            values.tier = tier;
        };

        useEffect(doMath, []);

        useDebounce(doMath, 1000, [values]);

        useDebounce(
            () => {
                onProUpdate?.(values);
            },
            1000,
            [values]
        );

        return null;
    };

    useEffect(() => {
        if (!isProTeam(role)) return;
        const getBrands = async () => {
            const response = await axiosInstant.get(searchBrand(""));
            if (!response.error) {
                const brandSet = response.data.rows.map((item) => ({
                    value: item.id,
                    brand: {
                        name: item.name
                    }
                }));
                brandSet.sort((a, b) => {
                    if (a.brand.name > b.brand.name) {
                        return 1;
                    }
                    if (a.brand.name < b.brand.name) {
                        return -1;
                    }
                    return 0;
                });
                setBrandList(brandSet);
            }
        };
        getBrands();
    }, [role]);

    const handleImageChange = (value) => {
        if (value.file && value.extension) {
            setNewAvatarFile(value);
        } else if (value.source) {
            setNewAvatarFile({ source: value.source, file: null, extension: null });
        }
    };

    const handleVideoAdd = (key, value) => {
        initValues[key] = value;
    };

    const mergePendingItemsBeforeSubmit = () => {
        const mergedExpertise = expertiseFormRef.current?.mergePendingItems() || expertise;
        const mergedInterests = interestsFormRef.current?.mergePendingItems() || interests;
        const mergedKeyPoints = keyPointsFormRef.current?.mergePendingItems() || keyPoints;
        const mergedProfessions = professionsFormRef.current?.mergePendingItems() || professions;

        // Update state immediately so UI reflects changes
        setExpertise(mergedExpertise);
        setInterests(mergedInterests);
        setKeyPoints(mergedKeyPoints);
        setProfessions(mergedProfessions);

        return { mergedExpertise, mergedInterests, mergedKeyPoints, mergedProfessions };
    };

    const submit = async (values, actions) => {
        const { mergedExpertise, mergedInterests, mergedKeyPoints, mergedProfessions } =
            mergePendingItemsBeforeSubmit();

        values.emailAddress = values.emailAddress.trim();
        values.password = values.password || "";

        values.professions = [...mergedProfessions];
        values.clients = [...clients];
        values.expertise = [...mergedExpertise];
        values.keyPoints = [...mergedKeyPoints];
        values.interests = [...mergedInterests];

        console.log("Submitting with data:", {
            expertise: values.expertise,
            interests: values.interests,
            keyPoints: values.keyPoints
        });

        if (referredBy) {
            values.referredBy = {
                displayName: referredBy.displayName,
                userid: referredBy.userid
            };
        }

        values.ethnicity = values.ethnicity || "No Response";

        const avatarUrl = await handleProfileImageUpdate(values, pro, (errorMsg) => {
            actions.setSubmitting(false);
        });

        if (avatarUrl === null && (newAvatarFile || (values.avatar && values.avatar.startsWith("data:image/")))) {
            return;
        }

        if (newAvatarFile && newAvatarFile.source && !values.avatar) {
            values.avatar = newAvatarFile.source;
        }

        try {
            const response = await axiosInstant.patch(`${config.API_URL}/users/me`, values);
            console.log("Save response:", response.data);
            toast.success("Profile saved successfully!");

            // Refresh the data if onProUpdate callback is provided
            if (onProUpdate) {
                onProUpdate(response.data);
            }

            actions.setSubmitting(false);
        } catch (error) {
            console.error("Profile update error:", error);
            console.error("Error response:", error.response?.data);
            const errorMessage = error?.response?.data;
            if (errorMessage === "EmailAlreadyExists") {
                toast.error("Email already exists");
            } else {
                toast.error("Failed to save profile. Please try again.");
            }
            actions.setSubmitting(false);
        }
    };

    const handleReferredChange = (e, value) => {
        setReferredBy(value);
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={initValues}
            enableReinitialize
            validationSchema={registrationSchema}
            onSubmit={async (values, actions) => {
                // Always merge pending items before submit
                const { mergedExpertise, mergedInterests, mergedKeyPoints, mergedProfessions } =
                    mergePendingItemsBeforeSubmit();

                // Update values with merged data
                values.expertise = [...mergedExpertise];
                values.interests = [...mergedInterests];
                values.keyPoints = [...mergedKeyPoints];
                values.professions = [...mergedProfessions];

                console.log("Submitting with data:", {
                    expertise: values.expertise,
                    interests: values.interests,
                    keyPoints: values.keyPoints,
                    professions: values.professions
                });

                // Call the appropriate submit handler
                if (handleSubmit) {
                    return handleSubmit(values, actions);
                } else {
                    return submit(values, actions);
                }
            }}
        >
            {({ isValid, isSubmitting, errors }) => (
                <Form className="w-full" noValidate>
                    <FormObserver />
                    {!create && <UnsavedChangesGuard />}
                    <Box className="flex flex-col gap-4 sm:gap-6 bg-surface border border-border-token rounded-2xl w-full p-3 sm:p-4 min-w-0">
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}>{selectedTab}</Typography>
                        <Divider />
                        {selectedTab === ProFormTabs.Basics && (
                            <FormBasics create={create} handleImageChange={handleImageChange} />
                        )}

                        {selectedTab === ProFormTabs.IntroVideos && (
                            <FormIntroVideos pro={pro} handleVideoAdd={handleVideoAdd} />
                        )}
                        {selectedTab === ProFormTabs.ContactInfo && <FormContactInfo />}
                        {selectedTab === ProFormTabs.Demographics && <FormDemographics />}
                        {selectedTab === ProFormTabs.Profession && (
                            <DragNDropProvider>
                                <FormProfessions
                                    ref={professionsFormRef}
                                    professions={professions}
                                    updateProfessions={setProfessions}
                                />
                            </DragNDropProvider>
                        )}
                        {selectedTab === ProFormTabs.ExpertQualification && <FormQualifications />}

                        {selectedTab === ProFormTabs.SocialReach && <FormSocialReach />}

                        {selectedTab === ProFormTabs.Activity && (
                            <FormActivity brandList={brandList} clients={clients} updateClients={setClients} />
                        )}

                        {selectedTab === ProFormTabs.CommunityEngagement && <FormCommunityEngagement />}

                        {selectedTab === ProFormTabs.Experties && (
                            <FormExpertise
                                ref={expertiseFormRef}
                                expertise={expertise}
                                updateExpertise={setExpertise}
                            />
                        )}

                        {selectedTab === ProFormTabs.Interests && (
                            <FormInterests
                                ref={interestsFormRef}
                                interests={interests}
                                updateInterests={setInterests}
                            />
                        )}

                        {selectedTab === ProFormTabs.KeyPoints && (
                            <DragNDropProvider>
                                <FormKeyPoints
                                    ref={keyPointsFormRef}
                                    keyPoints={keyPoints}
                                    updateKeyPoints={setKeyPoints}
                                />
                            </DragNDropProvider>
                        )}

                        {selectedTab === ProFormTabs.Referrals && (
                            <>
                                <FormRow count={2}>
                                    <Box className="flex gap-0 flex-col">
                                        <FieldLabel>Referred By</FieldLabel>
                                        <FieldUserAutoComplete
                                            onChange={handleReferredChange}
                                            defaultValue={referredBy}
                                            fullWidth
                                            className="bg-surface-2 rounded-xl"
                                            sx={{
                                                "& fieldset": { border: "none" },
                                                "& .Mui-focused": {
                                                    border: "1px solid var(--accent)",
                                                    backgroundColor: "var(--surface)"
                                                }
                                            }}
                                            InputProps={{
                                                className:
                                                    "border border-transparent hover:border-text-2 !py-0 m-[1px]"
                                            }}
                                            popupIcon={<IconArrowDown />}
                                        />
                                    </Box>
                                    <FieldInput name="referrals.length" label="Total Referrals" readOnly />
                                </FormRow>

                                <FormRow count={1}>
                                    <FieldInput name="referringSource" label="Where did you find us?" multiline />
                                </FormRow>
                            </>
                        )}

                        {selectedTab === ProFormTabs.MSA && <FormDocuments userId={pro?.userid} msaAgreementSigned={pro?.msaAgreementSigned ?? false} expertName={pro?.displayName || pro?.firstName || ""} />}
                    </Box>

                    {selectedTab !== ProFormTabs.MSA && (
                        <Box className="mt-6 w-full flex flex-col gap-2 items-stretch sm:items-end justify-end">
                            <Box className="flex w-full sm:w-auto">
                                <Button
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || !isValid}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    Save Changes
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Form>
            )}
        </Formik>
    );
}
